import type { CatalogLogo } from "@core-ui/ContextServices/CatalogLogoService/catalogLogoHooks";
import { ClientCacheZone, clientCache } from "@core-ui/cache/ClientCache";
import { useEffect, useReducer, useRef, useSyncExternalStore } from "react";

export type CatalogLogoSource = Exclude<CatalogLogo, { src: string }> | Blob;

const toCachedLogo = (source: CatalogLogoSource | null): CatalogLogo | null =>
	source instanceof Blob ? { src: URL.createObjectURL(source) } : source;

// A key that is still resolving (the other theme, say) keeps the logo it replaces on screen instead of blanking it.
const useLastResolvedLogo = (logo: CatalogLogo | null | undefined): CatalogLogo | null => {
	const lastResolved = useRef<CatalogLogo | null>(null);
	const shown = logo === undefined ? lastResolved.current : logo;

	useEffect(() => {
		lastResolved.current = shown;
	});

	return shown;
};

// Entering or leaving layout edit mode swaps the static tree for the dnd-kit one and remounts every card and folder,
// so their logos have to outlive the component that fetched them.
const useCachedLogo = (key: string | null, fetchLogo: () => Promise<CatalogLogoSource | null>): CatalogLogo | null => {
	const revision = useSyncExternalStore(clientCache.subscribe, clientCache.getRevision, clientCache.getRevision);
	const [, showLoadedLogo] = useReducer((loads: number) => loads + 1, 0);
	const logo = key ? clientCache.get<CatalogLogo | null>(ClientCacheZone.CatalogLogo, key) : null;

	// biome-ignore lint/correctness/useExhaustiveDependencies: `key` identifies what `fetchLogo` loads, `revision` reloads a cleared cache
	useEffect(() => {
		if (!key || clientCache.get(ClientCacheZone.CatalogLogo, key) !== undefined) return;

		void clientCache
			.getOrCreate(ClientCacheZone.CatalogLogo, key, async () => toCachedLogo(await fetchLogo()))
			.then(
				() => showLoadedLogo(),
				() => undefined,
			);
	}, [key, revision]);

	return useLastResolvedLogo(logo);
};

export default useCachedLogo;
